import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../api/queryKeys';
import { useRoentgenStore } from './roentgenStore';
import type {
  ChatMessage,
  SSEEvent,
  TextEvent,
  ToolCallEvent,
  ToolResultEvent,
  ConfirmRequiredEvent,
  ActionEvent,
  ErrorEvent,
  DoneEvent,
  SendMessageRequest,
  SessionHistoryResponse,
  CreateSessionResponse,
  SessionListItem,
  SessionListResponse,
  AssistantAction,
} from './types';

const MAX_MESSAGE_LENGTH = 4_000;
const BASE_URL = '/roentgen/api/chat';

type ToolPayload = Record<string, unknown>;

function buildPendingConfirmationFromMessages(
  messages: ChatMessage[],
): ConfirmRequiredEvent | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'confirmation') {
      continue;
    }

    const metadata = message.metadata;
    if (!metadata?.toolCallId || !metadata.tool || !metadata.confirmDescription || !metadata.expiresAt) {
      continue;
    }

    if ((metadata.confirmationStatus ?? 'pending') !== 'pending') {
      continue;
    }

    const expiresAtMs = Date.parse(metadata.expiresAt);
    if (!Number.isNaN(expiresAtMs) && expiresAtMs <= Date.now()) {
      continue;
    }

    return {
      type: 'confirm_required',
      toolCallId: metadata.toolCallId,
      tool: metadata.tool,
      args: metadata.args,
      description: metadata.confirmDescription,
      expiresAt: metadata.expiresAt,
    };
  }

  return null;
}

function getActionPayload(
  browserResult?: ToolPayload,
  result?: ToolPayload,
): ToolPayload | null {
  if (browserResult && typeof browserResult.action === 'string') {
    return browserResult;
  }

  if (result && typeof result.action === 'string') {
    return result;
  }

  return browserResult ?? result ?? null;
}

function getStudyObjectKeys(payload: ToolPayload | null): string[] {
  if (!payload) {
    return [];
  }

  const fromObjKey = typeof payload.objKey === 'string' && payload.objKey.trim()
    ? [payload.objKey]
    : [];
  const fromObjKeys = Array.isArray(payload.objKeys)
    ? payload.objKeys.filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0,
      )
    : [];

  return [...new Set([...fromObjKey, ...fromObjKeys])];
}

function getBritSession(): string {
  return localStorage.getItem('brit_sid') ?? '';
}

function makeHeaders(options?: { json?: boolean }): Record<string, string> {
  const headers: Record<string, string> = {
    'BRIT-Session': getBritSession(),
  };

  if (options?.json !== false) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

/**
 * Parse an SSE stream from a fetch Response.
 * Yields parsed SSEEvent objects.
 */
async function* parseSSEStream(
  response: Response,
): AsyncGenerator<SSEEvent> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let currentData = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith(': ')) continue; // Heartbeat ping — ignore
        if (line === ':') continue; // Bare comment — ignore

        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          currentData += line.slice(6);
        } else if (line === '') {
          if (currentEvent && currentData) {
            try {
              const parsed = JSON.parse(currentData);
              yield { type: currentEvent, ...parsed } as SSEEvent;
            } catch {
              // Malformed JSON — skip
            }
          }
          currentEvent = '';
          currentData = '';
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function useRoentgenChat() {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [pendingActions, setPendingActions] = useState<AssistantAction[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [closingSessionId, setClosingSessionId] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] =
    useState<ConfirmRequiredEvent | null>(null);
  const [error, setError] = useState<ErrorEvent | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeSessionId = useRoentgenStore((s) => s.activeSessionId);
  const setActiveSession = useRoentgenStore((s) => s.setActiveSession);

  const invalidateCachesForToolResult = useCallback(async (
    browserResult?: ToolPayload,
    result?: ToolPayload,
  ) => {
    const payload = getActionPayload(browserResult, result);
    const action = typeof payload?.action === 'string' ? payload.action : null;

    if (!action) {
      return;
    }

    if (action === 'sendMessage' || action === 'acknowledgeMessage') {
      await queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
      return;
    }

    if (action === 'retryFailedQueue') {
      await queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
      return;
    }

    if (!['hold', 'unhold', 'mark_urgent', 'assign', 'update_status'].includes(action)) {
      return;
    }

    const studyObjectKeys = getStudyObjectKeys(payload);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.studies.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.counters.all }),
      ...studyObjectKeys.map((objKey) =>
        queryClient.invalidateQueries({ queryKey: queryKeys.studies.detail(objKey) })),
    ]);
  }, [queryClient]);

  const listSessions = useCallback(async (): Promise<SessionListItem[]> => {
    setIsLoadingSessions(true);
    try {
      const res = await fetch(`${BASE_URL}/sessions?status=active&start=0&limit=20`, {
        headers: makeHeaders(),
      });

      if (!res.ok) {
        return [];
      }

      const data: SessionListResponse = await res.json();
      const nextSessions = data.sessions ?? [];
      setSessions(nextSessions);
      return nextSessions;
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    const britSession = getBritSession();
    if (!britSession) {
      return;
    }

    void listSessions().then((loadedSessions) => {
      if (!activeSessionId && loadedSessions.length > 0) {
        setActiveSession(loadedSessions[0].id);
      }
    });
  }, [activeSessionId, listSessions, setActiveSession]);

  const updateConfirmationMessage = useCallback(
    (toolCallId: string, patch: Record<string, unknown>) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.metadata?.toolCallId === toolCallId
            ? {
                ...message,
                metadata: {
                  ...message.metadata,
                  ...patch,
                },
              }
            : message,
        ),
      );
    },
    [],
  );

  /** Create a new chat session */
  const createSession = useCallback(
    async (title?: string): Promise<string> => {
      const britSession = getBritSession();
      if (!britSession) {
        throw new Error('Your PACS session is missing. Reload and sign in again.');
      }

      const res = await fetch(`${BASE_URL}/sessions`, {
        method: 'POST',
        headers: makeHeaders(),
        body: JSON.stringify({ title }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({
          error: { code: 'RPC_ERROR', message: 'Failed to create session' },
        }));
        throw new Error(err.error?.message ?? 'Failed to create session');
      }

      const data: CreateSessionResponse = await res.json();
      setActiveSession(data.id);
      void listSessions();
      return data.id;
    },
    [listSessions, setActiveSession],
  );

  /** Load session history (for reconnection or session switching) */
  const loadHistory = useCallback(async (sessionId: string) => {
    const res = await fetch(`${BASE_URL}/sessions/${sessionId}`, {
      headers: makeHeaders(),
    });

    if (!res.ok) return;

    const data: SessionHistoryResponse = await res.json();
    setActiveSession(sessionId);
    setMessages(data.messages);
    setPendingConfirmation(buildPendingConfirmationFromMessages(data.messages));
  }, [setActiveSession]);

  /** Send a message and process the SSE stream */
  const sendMessage = useCallback(
    async (
      content: string,
      context?: SendMessageRequest['context'],
    ): Promise<boolean> => {
      if (content.length > MAX_MESSAGE_LENGTH) {
        setError({
          type: 'error',
          code: 'VALIDATION_ERROR',
          message: `Message exceeds ${MAX_MESSAGE_LENGTH} character limit`,
          fatal: false,
        });
        return false;
      }

      const britSession = getBritSession();
      if (!britSession) {
        setError({
          type: 'error',
          code: 'SESSION_INVALID',
          message: 'Your PACS session is missing. Reload and sign in again.',
          fatal: true,
        });
        return false;
      }

      let sessionId = activeSessionId;
      try {
        if (!sessionId) {
          sessionId = await createSession();
        }
      } catch (err) {
        setError({
          type: 'error',
          code: 'SESSION_INVALID',
          message: (err as Error).message ?? 'Failed to create chat session',
          fatal: true,
        });
        return false;
      }

      // Add user message to local state immediately
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        sessionId,
        role: 'user',
        content,
        metadata: null,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setError(null);

      // Abort any existing stream
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch(
          `${BASE_URL}/sessions/${sessionId}/messages`,
          {
            method: 'POST',
            headers: { ...makeHeaders(), Accept: 'text/event-stream' },
            body: JSON.stringify({ content, context }),
            signal: controller.signal,
          },
        );

        if (res.status === 429) {
          setError({
            type: 'error',
            code: 'RATE_LIMITED',
            message: 'Roentgen is active in another tab',
            fatal: false,
          });
          setIsStreaming(false);
          return false;
        }

        if (!res.ok) {
          const err = await res.json().catch(() => ({
            error: { code: 'RPC_ERROR', message: 'Stream failed' },
          }));
          setError({
            type: 'error',
            code: err.error?.code ?? 'RPC_ERROR',
            message: err.error?.message ?? 'Stream failed',
            fatal: true,
          });
          setIsStreaming(false);
          return false;
        }

        // Create initial assistant message
        const assistantMsgId = crypto.randomUUID();
        const assistantMessage: ChatMessage = {
          id: assistantMsgId,
          sessionId,
          role: 'assistant',
          content: '',
          metadata: null,
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Process SSE stream
        for await (const event of parseSSEStream(res)) {
          switch (event.type) {
            case 'text': {
              const { delta } = event as TextEvent;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: (m.content ?? '') + delta }
                    : m,
                ),
              );
              break;
            }

            case 'tool_call': {
              const { toolCallId, tool, args } = event as ToolCallEvent;
              const toolMsg: ChatMessage = {
                id: toolCallId,
                sessionId,
                role: 'tool_call',
                content: null,
                metadata: { toolCallId, tool, args },
                createdAt: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, toolMsg]);
              break;
            }

            case 'tool_result': {
              const { toolCallId, result, browserResult } =
                event as ToolResultEvent;
              void invalidateCachesForToolResult(
                browserResult as ToolPayload | undefined,
                result as ToolPayload | undefined,
              );
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === toolCallId
                    ? {
                        ...m,
                        role: 'tool_result' as const,
                        metadata: {
                          ...m.metadata,
                          result,
                          browserResult,
                        },
                      }
                    : m,
                ),
              );
              break;
            }

            case 'confirm_required': {
              const confirmEvent = event as ConfirmRequiredEvent;
              setPendingConfirmation(confirmEvent);
              const confirmMsg: ChatMessage = {
                id: `${confirmEvent.toolCallId}:confirmation`,
                sessionId,
                role: 'confirmation',
                content: null,
                metadata: {
                  toolCallId: confirmEvent.toolCallId,
                  tool: confirmEvent.tool,
                  confirmDescription: confirmEvent.description,
                  confirmationStatus: 'pending',
                  expiresAt: confirmEvent.expiresAt,
                },
                createdAt: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, confirmMsg]);
              break;
            }

            case 'action': {
              const { actions } = event as ActionEvent;
              const filterActions = actions.filter(
                (action) => action.type === 'apply_filter',
              );
              setPendingActions(filterActions);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, metadata: { ...m.metadata, actions } }
                    : m,
                ),
              );
              break;
            }

            case 'error': {
              const errEvent = event as ErrorEvent;
              setError(errEvent);
              if (errEvent.fatal) {
                setIsStreaming(false);
                return false;
              }
              break;
            }

            case 'done': {
              const { messageId } = event as DoneEvent;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, id: messageId } : m,
                ),
              );
              break;
            }
          }
        }
        return true;
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError({
            type: 'error',
            code: 'RPC_ERROR',
            message: (err as Error).message ?? 'Connection lost',
            fatal: true,
          });
        }
        return false;
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [activeSessionId, createSession, invalidateCachesForToolResult],
  );

  /** Confirm a pending write tool action */
  const confirmAction = useCallback(
    async (toolCallId: string) => {
      if (!activeSessionId) return;

      try {
        const res = await fetch(
          `${BASE_URL}/sessions/${activeSessionId}/confirm/${toolCallId}`,
          { method: 'POST', headers: makeHeaders({ json: false }) },
        );

        if (!res.ok) {
          const err = await res.json().catch(() => ({
            error: { code: 'RPC_ERROR', message: 'Confirm failed' },
          }));
          const code = err.error?.code ?? 'RPC_ERROR';
          const message = err.error?.message ?? 'Confirm failed';

          setError({
            type: 'error',
            code,
            message,
            fatal: false,
          });
          setPendingConfirmation(null);
          updateConfirmationMessage(toolCallId, code === 'CONFIRMATION_EXPIRED'
            ? {
                confirmationStatus: 'expired' as const,
                expiredAt: new Date().toISOString(),
              }
            : {
                confirmationStatus: 'error' as const,
                errorAt: new Date().toISOString(),
                confirmationErrorCode: code,
                confirmationErrorMessage: message,
              });
          return;
        }

        setPendingConfirmation(null);
        updateConfirmationMessage(toolCallId, {
          confirmationStatus: 'confirmed' as const,
          confirmedAt: new Date().toISOString(),
        });
      } catch (err) {
        setPendingConfirmation(null);
        updateConfirmationMessage(toolCallId, {
          confirmationStatus: 'error' as const,
          errorAt: new Date().toISOString(),
          confirmationErrorCode: 'RPC_ERROR',
          confirmationErrorMessage: (err as Error).message ?? 'Confirm failed',
        });
        setError({
          type: 'error',
          code: 'RPC_ERROR',
          message: (err as Error).message ?? 'Confirm failed',
          fatal: false,
        });
      }
    },
    [activeSessionId, updateConfirmationMessage],
  );

  /** Reject a pending write tool action */
  const rejectAction = useCallback(
    async (toolCallId: string) => {
      if (!activeSessionId) return;

      try {
        const res = await fetch(
          `${BASE_URL}/sessions/${activeSessionId}/reject/${toolCallId}`,
          { method: 'POST', headers: makeHeaders({ json: false }) },
        );

        if (!res.ok) {
          const err = await res.json().catch(() => ({
            error: { code: 'RPC_ERROR', message: 'Reject failed' },
          }));
          const code = err.error?.code ?? 'RPC_ERROR';
          const message = err.error?.message ?? 'Reject failed';

          setError({
            type: 'error',
            code,
            message,
            fatal: false,
          });
          setPendingConfirmation(null);
          updateConfirmationMessage(toolCallId, code === 'CONFIRMATION_EXPIRED'
            ? {
                confirmationStatus: 'expired' as const,
                expiredAt: new Date().toISOString(),
              }
            : {
                confirmationStatus: 'error' as const,
                errorAt: new Date().toISOString(),
                confirmationErrorCode: code,
                confirmationErrorMessage: message,
              });
          return;
        }

        setPendingConfirmation(null);
        updateConfirmationMessage(toolCallId, {
          confirmationStatus: 'rejected' as const,
          rejectedAt: new Date().toISOString(),
        });
      } catch (err) {
        setPendingConfirmation(null);
        updateConfirmationMessage(toolCallId, {
          confirmationStatus: 'error' as const,
          errorAt: new Date().toISOString(),
          confirmationErrorCode: 'RPC_ERROR',
          confirmationErrorMessage: (err as Error).message ?? 'Reject failed',
        });
        setError({
          type: 'error',
          code: 'RPC_ERROR',
          message: (err as Error).message ?? 'Reject failed',
          fatal: false,
        });
      }
    },
    [activeSessionId, updateConfirmationMessage],
  );

  /** Cancel the current streaming request */
  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /** Clear all messages and reset state */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setPendingActions([]);
    setError(null);
    setPendingConfirmation(null);
  }, []);

  const closeSession = useCallback(async (sessionId: string) => {
    setClosingSessionId(sessionId);
    setError(null);

    try {
      const res = await fetch(`${BASE_URL}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: makeHeaders({ json: false }),
      });

      if (!res.ok && res.status !== 404) {
        const err = await res.json().catch(() => ({
          error: { code: 'RPC_ERROR', message: 'Failed to close session' },
        }));
        throw new Error(err.error?.message ?? 'Failed to close session');
      }

      const nextSessions = await listSessions();
      const currentActiveSessionId = useRoentgenStore.getState().activeSessionId;

      if (currentActiveSessionId !== sessionId) {
        return;
      }

      const nextActiveSessionId = nextSessions[0]?.id ?? null;
      clearMessages();
      setActiveSession(nextActiveSessionId);
      if (nextActiveSessionId) {
        await loadHistory(nextActiveSessionId);
      }
    } catch (err) {
      const message = (err as Error).message ?? 'Failed to close session';
      setError({
        type: 'error',
        code: 'RPC_ERROR',
        message,
        fatal: false,
      });
      throw err;
    } finally {
      setClosingSessionId((current) => (current === sessionId ? null : current));
    }
  }, [clearMessages, listSessions, loadHistory, setActiveSession]);

  const switchSession = useCallback(async (sessionId: string) => {
    clearMessages();
    await loadHistory(sessionId);
  }, [clearMessages, loadHistory]);

  const clearPendingActions = useCallback(() => {
    setPendingActions([]);
  }, []);

  return {
    sessions,
    isLoadingSessions,
    pendingActions,
    messages,
    isStreaming,
    closingSessionId,
    pendingConfirmation,
    error,
    listSessions,
    switchSession,
    clearPendingActions,
    sendMessage,
    confirmAction,
    rejectAction,
    createSession,
    closeSession,
    loadHistory,
    cancelStream,
    clearMessages,
  };
}
